import { useEffect, useRef } from "react";

/**
 * Animated network hero background — interconnected nodes with
 * flowing data packets, particles, expanding rings and grid.
 * Ported from a standalone HTML/canvas animation into React.
 */
export const HeroNetworkCanvas = ({ className = "" }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = containerRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");

    let rafId = 0;
    let t = 0;
    let ringTimer = 0;

    const PALETTE = {
      bg: "#020d1a",
      nodeLine: "#1a5580",
      blue2: "#1272b8",
      teal1: "#0a6e6e",
      teal2: "#14b8b8",
      cyan: "#38e8e8",
      particle: "#a0e0f0",
    };

    const NODE_DEFS = [
      { rx: 0.5,  ry: 0.5,  r: 0.042, color: PALETTE.teal2,    glowColor: "#00e5e5", size: 1.4 },
      { rx: 0.22, ry: 0.32, r: 0.028, color: PALETTE.blue2,    glowColor: "#1490e8", size: 1.0 },
      { rx: 0.78, ry: 0.32, r: 0.028, color: PALETTE.blue2,    glowColor: "#1490e8", size: 1.0 },
      { rx: 0.22, ry: 0.68, r: 0.028, color: PALETTE.blue2,    glowColor: "#1490e8", size: 1.0 },
      { rx: 0.78, ry: 0.68, r: 0.028, color: PALETTE.blue2,    glowColor: "#1490e8", size: 1.0 },
      { rx: 0.5,  ry: 0.18, r: 0.020, color: PALETTE.teal1,    glowColor: "#00cfcf", size: 0.8 },
      { rx: 0.5,  ry: 0.82, r: 0.020, color: PALETTE.teal1,    glowColor: "#00cfcf", size: 0.8 },
      { rx: 0.1,  ry: 0.5,  r: 0.016, color: PALETTE.cyan,     glowColor: "#38e8e8", size: 0.6 },
      { rx: 0.9,  ry: 0.5,  r: 0.016, color: PALETTE.cyan,     glowColor: "#38e8e8", size: 0.6 },
      { rx: 0.15, ry: 0.18, r: 0.013, color: PALETTE.nodeLine, glowColor: "#1a5580", size: 0.5 },
      { rx: 0.85, ry: 0.18, r: 0.013, color: PALETTE.nodeLine, glowColor: "#1a5580", size: 0.5 },
      { rx: 0.15, ry: 0.82, r: 0.013, color: PALETTE.nodeLine, glowColor: "#1a5580", size: 0.5 },
      { rx: 0.85, ry: 0.82, r: 0.013, color: PALETTE.nodeLine, glowColor: "#1a5580", size: 0.5 },
    ];

    const EDGES = [
      [0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [0, 6], [0, 7], [0, 8],
      [1, 5], [2, 5], [3, 6], [4, 6],
      [1, 7], [3, 7], [2, 8], [4, 8],
      [5, 9], [5, 10], [6, 11], [6, 12],
      [1, 9], [2, 10], [3, 11], [4, 12],
    ];

    const W = () => canvas.width;
    const H = () => canvas.height;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const getNode = (n) => {
      const d = NODE_DEFS[n];
      const w = wrap.clientWidth, h = wrap.clientHeight;
      return { x: d.rx * w, y: d.ry * h, r: d.r * Math.min(w, h) };
    };

    const hex2 = (v) =>
      Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0");

    const makePacket = (edgeIndex) => {
      const ei = edgeIndex % EDGES.length;
      const [a, b] = EDGES[ei];
      const fwd = Math.random() > 0.4;
      return {
        edge: ei,
        src: fwd ? a : b,
        dst: fwd ? b : a,
        t: Math.random(),
        speed: 0.0012 + Math.random() * 0.0018,
        color: Math.random() > 0.5 ? PALETTE.cyan : PALETTE.teal2,
        size: 2.5 + Math.random() * 2,
      };
    };

    const packets = Array.from({ length: 28 }, () =>
      makePacket(Math.floor(Math.random() * EDGES.length))
    );

    const parts = Array.from({ length: 55 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: 0.5 + Math.random() * 1.2,
      vx: (Math.random() - 0.5) * 0.00012,
      vy: (Math.random() - 0.5) * 0.00012,
      alpha: 0.15 + Math.random() * 0.35,
    }));

    let rings = [];
    const spawnRing = () =>
      rings.push({ rx: 0.5, ry: 0.5, rad: 0, alpha: 0.18, speed: 0.0006 });
    spawnRing();

    const nodeGlow = (x, y, r, color, intensity) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r * 3.5);
      g.addColorStop(0, color + hex2(intensity * 80));
      g.addColorStop(0.4, color + hex2(intensity * 30));
      g.addColorStop(1, color + "00");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r * 3.5, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawNode = (n) => {
      const { x, y, r } = getNode(n);
      const d = NODE_DEFS[n];
      const breathe = 1 + Math.sin(t * 1.4 + n * 0.9) * 0.06 * d.size;
      const rr = r * breathe;
      const pulse = Math.max(0, Math.sin(t * 1.8 + n * 1.1));
      nodeGlow(x, y, rr, d.glowColor, 0.6 + pulse * 0.4);
      const g2 = ctx.createRadialGradient(x - rr * 0.3, y - rr * 0.3, 0, x, y, rr);
      g2.addColorStop(0, "#ffffff18");
      g2.addColorStop(0.5, d.color + "cc");
      g2.addColorStop(1, d.color + "88");
      ctx.beginPath();
      ctx.arc(x, y, rr, 0, Math.PI * 2);
      ctx.fillStyle = g2;
      ctx.fill();
      ctx.strokeStyle = d.color + "cc";
      ctx.lineWidth = 1;
      ctx.stroke();
      if (n === 0) {
        ctx.beginPath();
        ctx.arc(x, y, rr * 0.35, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff44";
        ctx.fill();
      }
    };

    const getEdgeCurve = (a, b) => {
      const na = getNode(a), nb = getNode(b);
      const mx = (na.x + nb.x) / 2, my = (na.y + nb.y) / 2;
      const dx = nb.x - na.x, dy = nb.y - na.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      return {
        x1: na.x, y1: na.y,
        cx: mx + (-dy / len) * len * 0.12,
        cy: my + (dx / len) * len * 0.12,
        x2: nb.x, y2: nb.y,
      };
    };

    const drawEdge = (a, b) => {
      const c = getEdgeCurve(a, b);
      ctx.beginPath();
      ctx.moveTo(c.x1, c.y1);
      ctx.quadraticCurveTo(c.cx, c.cy, c.x2, c.y2);
      ctx.strokeStyle = PALETTE.nodeLine + "55";
      ctx.lineWidth = 0.8;
      ctx.stroke();
    };

    const quadPoint = (c, tt) => ({
      x: (1 - tt) * (1 - tt) * c.x1 + 2 * (1 - tt) * tt * c.cx + tt * tt * c.x2,
      y: (1 - tt) * (1 - tt) * c.y1 + 2 * (1 - tt) * tt * c.cy + tt * tt * c.y2,
    });

    const drawPacket = (p) => {
      const c = getEdgeCurve(p.src, p.dst);
      const { x, y } = quadPoint(c, p.t);
      const gp = ctx.createRadialGradient(x, y, 0, x, y, p.size * 3);
      gp.addColorStop(0, p.color + "ff");
      gp.addColorStop(0.4, p.color + "88");
      gp.addColorStop(1, p.color + "00");
      ctx.beginPath();
      ctx.arc(x, y, p.size * 3, 0, Math.PI * 2);
      ctx.fillStyle = gp;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x, y, p.size * 0.9, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
    };

    const drawGrid = () => {
      ctx.strokeStyle = "#0a2a4422";
      ctx.lineWidth = 0.5;
      const cols = 22, rows = 13;
      const w = wrap.clientWidth, h = wrap.clientHeight;
      for (let i = 0; i <= cols; i++) {
        const x = (i / cols) * w;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let j = 0; j <= rows; j++) {
        const y = (j / rows) * h;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }
    };

    const frame = () => {
      t += 0.012;
      ringTimer += 0.012;
      if (ringTimer > 5.5) {
        spawnRing();
        ringTimer = 0;
      }

      const w = wrap.clientWidth, h = wrap.clientHeight;

      ctx.fillStyle = PALETTE.bg;
      ctx.fillRect(0, 0, w, h);

      const vig = ctx.createRadialGradient(w * 0.5, h * 0.5, 0, w * 0.5, h * 0.5, w * 0.6);
      vig.addColorStop(0, "#041525aa");
      vig.addColorStop(1, "#010a1400");
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, w, h);

      drawGrid();

      for (const p of parts) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = 1;
        if (p.x > 1) p.x = 0;
        if (p.y < 0) p.y = 1;
        if (p.y > 1) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
        ctx.fillStyle = PALETTE.particle + hex2(p.alpha * 255);
        ctx.fill();
      }

      rings = rings.filter((r) => r.alpha > 0.005);
      for (const r of rings) {
        const x = r.rx * w, y = r.ry * h;
        const rad = r.rad * Math.min(w, h);
        ctx.beginPath();
        ctx.arc(x, y, rad, 0, Math.PI * 2);
        ctx.strokeStyle = PALETTE.teal2 + hex2(r.alpha * 255);
        ctx.lineWidth = 0.8;
        ctx.stroke();
        r.rad += r.speed;
        r.alpha *= 0.994;
      }

      for (const [a, b] of EDGES) drawEdge(a, b);

      for (const p of packets) {
        p.t += p.speed;
        if (p.t >= 1) {
          const ei = Math.floor(Math.random() * EDGES.length);
          Object.assign(p, makePacket(ei), { t: 0 });
        }
        drawPacket(p);
      }

      for (let i = 0; i < NODE_DEFS.length; i++) drawNode(i);

      rafId = requestAnimationFrame(frame);
    };

    rafId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${className}`}
      data-testid="hero-network-canvas"
      style={{ background: "#020d1a" }}
    >
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
};
